import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, OAuthProvider } from 'firebase/auth';

// Firebase configuration - loaded from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Point the client at a local Firebase Auth emulator when one is configured, so
// an automated pass can sign in as a specific role without anyone typing
// credentials. Unset by default: a normal build authenticates against real
// Firebase exactly as before.
//
// The port is not fixed at Firebase's 9099 default. It follows this checkout's
// PORT_OFFSET (9099 + offset), because two projects both sitting on 9099 do not
// fail loudly: the second one authenticates against the first one's emulator,
// sign-in succeeds, and the server then finds no profile for a UID from another
// project's fixtures. scripts/development/start-container derives this value.
//
// The host is required to be loopback. Without that check a stray or injected
// value could send real users' credentials to an arbitrary host, so the variable
// is treated as untrusted input even though it is developer-supplied.
const emulatorHost = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
if (emulatorHost) {
    if (/^(127\.0\.0\.1|localhost|\[::1\]):\d+$/.test(emulatorHost)) {
        connectAuthEmulator(auth, `http://${emulatorHost}`, {
            disableWarnings: true,
        });
    } else {
        // Loud rather than silent: a misconfigured host means the pass is
        // authenticating somewhere unintended, which must not look like success.
        throw new Error(
            `REACT_APP_FIREBASE_AUTH_EMULATOR_HOST must be a loopback address, got "${emulatorHost}"`
        );
    }
}

// Microsoft (Entra ID) sign-in. MSU Denver accounts live in a Microsoft
// tenant, so this is the path to signing in with university credentials.
// Firebase performs the OAuth exchange on the auth domain, which is why the
// redirect URI registered with Microsoft is
// https://<authDomain>/__/auth/handler and never a route on this site.
//
// The tenant decides which directory Microsoft signs against and what the
// account picker offers. It is a request the bundle makes, not a boundary:
// the app registration's supported account types are what confine sign-in
// to one organisation. The server supplies the value per environment
// (/api/auth/config); the build-time variable is the fallback before that
// answer arrives, and `common` admits any account so a test registration
// works before the university's tenant is configured.
export const MICROSOFT_PROVIDER_ID = 'microsoft.com';

export function createMicrosoftProvider(
    tenant = process.env.REACT_APP_MICROSOFT_TENANT || 'common'
) {
    const provider = new OAuthProvider(MICROSOFT_PROVIDER_ID);
    provider.setCustomParameters({
        tenant,
        // Always show the account picker. On a shared lab computer the browser
        // must not silently reuse whichever account signed in last.
        prompt: 'select_account',
    });
    return provider;
}

export default app;
